import styles from './deactiveAccountDialog.module.scss'
import UserInput from '@/components/_custom/UserInput/userInput.tsx'
import validator from 'validator'
import { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/modules/context/AuthContext.tsx'
import Dialog from '@/components/_custom/Dialog/Dialog.tsx'
import DialogHeader from '@/components/_custom/DialogHeader/DialogHeader.tsx'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon.tsx'
import ConfirmDeactiveAccountDialog from './confirmDeactiveAccountDialog.tsx'
import DialogBody from '@/components/_custom/DialogBody/DialogBody.tsx'
import { Mail } from '@mui/icons-material'
import { useMutation } from '@apollo/client'
import { CONTACT_DAYCARE_FOR_DELETE_ACCOUNT } from '@/modules/API/account/accountHandler.ts'
import { useNotification } from '@/modules/context/NotificationContext.tsx'

interface DeactiveAccountDialogProps {
    open: boolean
    handleClose: () => void
    userId: number
    isParent: boolean
}

const DeactiveAccountDialog: FunctionComponent<DeactiveAccountDialogProps> = ({
    open,
    handleClose,
    userId,
    isParent
}: DeactiveAccountDialogProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const { showNotificationSuccess, showNotificationError } = useNotification()

    const [emailDeactivation, setEmailDeactivation] = useState<string>('')

    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false)

    const [contactUserDaycare, { loading: loadingContact }] = useMutation(CONTACT_DAYCARE_FOR_DELETE_ACCOUNT, {
        variables: {
            userId
        },
        onCompleted: () => {
            showNotificationSuccess(t('daycareContactSuccess'))
            handleClose()
        },
        onError: () => {
            showNotificationError(t('daycareContactError'))
            handleClose()
        }
    })

    return (
        <Dialog open={open} handleClose={handleClose}>
            <DialogHeader title={t('deleteMyAccount')} parent={isParent} />
            <DialogBody className={styles.DialogBody}>
                {user &&
                (user.currentDaycare === undefined || user.currentDaycare === null) &&
                user.daycares.length === 0 ? (
                    <>
                        <div className={`${styles.TextContainer} ${styles.AddMarginBottom}`}>
                            <p className={styles.Text}>{t('areYouSureToDeleteAccount')}</p>
                            <p className={styles.Text}>{t('enterYourEmailBelow')}</p>
                        </div>
                        <div className={styles.InputContainer}>
                            <div className={styles.Input}>
                                <UserInput
                                    value={emailDeactivation}
                                    setValue={setEmailDeactivation}
                                    required={true}
                                    label={t('email')}
                                    className={styles.UserInput}
                                    type={'text'}
                                    verify={'email'}
                                    unallowedCharacters={'email'}
                                    parent={isParent}
                                />
                            </div>
                        </div>
                        <div className={`${styles.TextContainer} ${styles.AddMarginBottom}`}>
                            <p className={styles.Text}>{t('noRollBack')}</p>
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                onClick={handleClose}
                                className={styles.Button}
                                parent={isParent}
                            />
                            <ButtonTextIcon
                                type={'delete'}
                                onClick={() => setConfirmDialogOpen(true)}
                                className={styles.Button}
                                disabled={!(user?.email === emailDeactivation) || !validator.isEmail(emailDeactivation)}
                                parent={isParent}
                            />
                        </div>
                        {isConfirmDialogOpen && (
                            <ConfirmDeactiveAccountDialog
                                open={isConfirmDialogOpen}
                                handleClose={() => setConfirmDialogOpen(false)}
                                email={emailDeactivation}
                                userId={userId}
                                isParent={isParent}
                            />
                        )}
                    </>
                ) : (
                    <>
                        <div className={styles.TextContainer}>
                            <p className={styles.Text}>{t('uCantDeleteYourAccount')}</p>
                            <p className={styles.Text}>
                                {t('uAreAttachedTo')}
                                <span className={isParent ? styles.SpanParent : styles.SpanDaycare}>
                                    {' '}
                                    {user?.daycares.length || '0'}
                                    {' '}
                                </span>
                                {t(user?.daycares.length === 1 ? 'structure' : 'structures').toLowerCase()}
                                {'.'}
                            </p>
                        </div>
                        <div className={styles.BreakLine}></div>
                        <div className={`${styles.TextContainer} ${styles.AddMarginBottom}`}>
                            <p className={styles.Text}>
                                {user?.daycares && user.daycares.length <= 1
                                    ? t('clickOnThisButtonForContactDaycare')
                                    : t('clickOnThisButtonForContactDaycares')}
                            </p>
                        </div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'custom'}
                                customIcon={<Mail />}
                                customText={
                                    user?.daycares && user.daycares.length <= 1
                                        ? t('contactMyDaycare')
                                        : t('contactMyDaycares')
                                }
                                onClick={contactUserDaycare}
                                className={styles.Button}
                                parent={isParent}
                                disabled={loadingContact}
                            />
                        </div>
                        <div className={styles.BreakLine}></div>
                        <div className={styles.ButtonContainer}>
                            <ButtonTextIcon
                                type={'close'}
                                onClick={handleClose}
                                className={styles.Button}
                                parent={isParent}
                            />
                        </div>
                        {isConfirmDialogOpen && (
                            <ConfirmDeactiveAccountDialog
                                open={isConfirmDialogOpen}
                                handleClose={() => setConfirmDialogOpen(false)}
                                email={emailDeactivation}
                                userId={userId}
                                isParent={isParent}
                            />
                        )}
                    </>
                )}
            </DialogBody>
        </Dialog>
    )
}

export default DeactiveAccountDialog
